export interface EndpointInfo {
  endpointName: string;
  endpointVersion: string;
}

/**
 * Parses an EndpointType string to extract the endpoint name and version.
 *
 * Examples:
 * - "Sorenson Videophone ntouch Mac/9.4.905 SSV/5"
 *   → { endpointName: "sorenson videophone ntouch mac", endpointVersion: "9.4.905" }
 * - "Sorenson Videophone AndroidMVRS/9.3.12.25 SSV/5"
 *   → { endpointName: "sorenson videophone androidmvrs", endpointVersion: "9.3.12.25" }
 *
 * @param endpointType - The full endpoint type string
 * @returns Object containing separated endpoint name (lowercase) and version
 */
export function parseEndpointInfo(endpointType: string): EndpointInfo {
  if (!endpointType) {
    return {
      endpointName: '',
      endpointVersion: '',
    };
  }

  // Match pattern: endpoint_name/version optional_additional_info
  // We want to capture everything before the first slash as the endpoint name
  // and everything between the first slash and either the next space or end of string as the version
  // Using indexOf for safer parsing to avoid ReDoS vulnerabilities
  const slashIndex = endpointType.indexOf('/');

  if (slashIndex === -1) {
    // If no slash found, treat the entire string as the endpoint name
    return {
      endpointName: endpointType.trim().toLowerCase(),
      endpointVersion: '',
    };
  }

  const endpointName = endpointType.substring(0, slashIndex).trim();
  const remainingPart = endpointType.substring(slashIndex + 1).trim();

  // Extract version - everything up to the first space or end of string
  const spaceIndex = remainingPart.indexOf(' ');
  const versionPart =
    spaceIndex === -1 ? remainingPart : remainingPart.substring(0, spaceIndex);

  // Remove any trailing SSV/X patterns from the version, limiting whitespace to 1–20 spaces for safety
  const version = versionPart.replace(/\s{1,20}SSV\/\d+$/, '');

  return {
    endpointName: endpointName.toLowerCase(),
    endpointVersion: version.trim(),
  };
}
